import querystring from 'querystring';
import axios, { type AxiosResponse } from 'axios';
import { type Endpoints } from '../constants/endpoints';
import { type Requester } from './index';
import {
  type LastHourDspStatisticsResponse,
  type LastHourSspStatisticsResponse,
  type V2StatisticsParams,
  type V2StatisticsResponse,
} from '../types/finances';

type QueryValue = string | number | undefined;

const toQueryValue = (val?: string | number | number[] | string[]): QueryValue => {
  if (val === undefined) return undefined;
  if (Array.isArray(val)) return val.join(',');
  return val;
};

const buildQueryParams = (params: V2StatisticsParams): Record<string, string | number> => {
  const queryParams: Record<string, string | number> = {
    from: params.from,
    to: params.to,
  };

  const format = toQueryValue(params.format);
  if (format !== undefined) {
    queryParams.format = format;
  }

  const groupBy = toQueryValue(params.groupBy);
  if (groupBy !== undefined) {
    queryParams.groupBy = groupBy;
  }

  const orderBy = toQueryValue(params.orderBy);
  if (orderBy !== undefined) {
    queryParams.orderBy = orderBy;
  }

  const orderDirection = toQueryValue(params.orderDirection);
  if (orderDirection !== undefined) {
    queryParams.orderDirection = orderDirection;
  }

  const sspId = toQueryValue(params.sspId);
  if (sspId !== undefined) {
    queryParams.sspId = sspId;
  }

  const dspId = toQueryValue(params.dspId);
  if (dspId !== undefined) {
    queryParams.dspId = dspId;
  }

  const companyId = toQueryValue(params.companyId);
  if (companyId !== undefined) {
    queryParams.companyId = companyId;
  }

  const extraCols = toQueryValue(params.extraCols);
  if (extraCols !== undefined) {
    queryParams.extraCols = extraCols;
  }

  if (typeof params.limit === 'number') {
    queryParams.limit = params.limit;
  }

  if (typeof params.offset === 'number') {
    queryParams.offset = params.offset;
  }

  return queryParams;
};

const extractFilename = (contentDispositionHeader?: string): string | undefined => {
  if (!contentDispositionHeader) return undefined;

  const utf8Filename = contentDispositionHeader.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Filename?.[1]) {
    try {
      return decodeURIComponent(utf8Filename[1]);
    } catch (error) {
      // ignore decode errors and fallback to standard filename
    }
  }

  const regularFilename = contentDispositionHeader.match(/filename="?([^";]+)"?/i);
  if (regularFilename?.[1]) {
    return regularFilename[1];
  }

  return undefined;
};

interface ExportV2StatisticsResponse {
  blob: Blob;
  filename?: string;
  totalCount?: number;
}

export function createStatisticsV2({ authorizedGET }: Requester, endpoints: Endpoints) {
  const getExtraCols = async (): Promise<string[]> => {
    const response = await authorizedGET<{ columns: Array<{ name: string }> }>(endpoints.statistics.v2ExtraCols);
    return response.columns.map((c) => c.name);
  };

  const getV2Statistics = async (params: V2StatisticsParams): Promise<V2StatisticsResponse> => {
    const url = `${endpoints.statistics.v2}?${querystring.stringify(buildQueryParams(params))}`;
    return authorizedGET<V2StatisticsResponse>(url);
  };

  const getLastHourSspStatistics = (): Promise<LastHourSspStatisticsResponse> => {
    return authorizedGET<LastHourSspStatisticsResponse>(endpoints.statistics.v2LastHourSsps);
  };

  const getLastHourDspStatistics = (): Promise<LastHourDspStatisticsResponse> => {
    return authorizedGET<LastHourDspStatisticsResponse>(endpoints.statistics.v2LastHourDsps);
  };

  const exportV2Statistics = async (params: V2StatisticsParams): Promise<ExportV2StatisticsResponse> => {
    const url = `${endpoints.statistics.v2Export}?${querystring.stringify(buildQueryParams(params))}`;

    const headers: Record<string, string> = {
      'X-Version': 'v2',
    };

    const token = window.localStorage.getItem('accessToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response: AxiosResponse<Blob> = await axios.get(url, {
      headers,
      responseType: 'blob',
    });

    const filename = extractFilename(response.headers['content-disposition']);
    const totalCountHeader = response.headers['x-total-count'];
    const parsedTotalCount =
      typeof totalCountHeader === 'string'
        ? Number(totalCountHeader)
        : Array.isArray(totalCountHeader)
          ? Number(totalCountHeader[0])
          : undefined;

    const totalCount = Number.isFinite(parsedTotalCount) ? Number(parsedTotalCount) : undefined;

    return {
      blob: response.data,
      filename,
      totalCount,
    };
  };

  const getExportUrl = (params: V2StatisticsParams): string => {
    return `${endpoints.statistics.v2Export}?${querystring.stringify(buildQueryParams(params))}`;
  };

  return {
    getExtraCols,
    getV2Statistics,
    getLastHourDspStatistics,
    getLastHourSspStatistics,
    exportV2Statistics,
    getExportUrl,
  };
}
